import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/Blog.css";
import { getBlogPosts, BlogPost } from "@/modules/blog/blogService";
import { Calendar, Search, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";


const allStates = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

const normalizeStateName = (input: string): string | null => {
  const lowerInput = input.toLowerCase();
  return allStates.find((state) => state.toLowerCase() === lowerInput) || null;
};

const reverseGeocode = async (lat: number, lon: number): Promise<string | null> => {
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`);
    const data = await res.json();
    return normalizeStateName(data?.address?.state || "");
  } catch (err) {
    console.error("Geocoding failed:", err);
    return null;
  }
};

export const stateImages = {
  Arizona: [
    "/images-webp/blogimages/arizona/arizona1.webp",
    "/images-webp/blogimages/arizona/arizona2.webp",
    "/images-webp/blogimages/arizona/arizona3.webp",
    "/images-webp/blogimages/arizona/arizona4.webp",
    "/images-webp/blogimages/arizona/arizona5.webp",
    "/images-webp/blogimages/arizona/arizona6.webp",
    "/images-webp/blogimages/arizona/arizona7.webp",
    "/images-webp/blogimages/arizona/arizona8.webp",
    "/images-webp/blogimages/arizona/arizona9.webp",
  ],
  California: [
    "/images-webp/blogimages/california/cali1.webp",
    "/images-webp/blogimages/california/cali2.webp",
    "/images-webp/blogimages/california/cali3.webp",
    "/images-webp/blogimages/california/cali4.webp",
    "/images-webp/blogimages/california/cali5.webp",
    "/images-webp/blogimages/california/cali6.webp",
    "/images-webp/blogimages/california/cali7.webp",
    "/images-webp/blogimages/california/cali8.webp",
    "/images-webp/blogimages/california/cali9.webp",
    "/images-webp/blogimages/california/cali10.webp",
    "/images-webp/blogimages/california/cali11.webp",
    "/images-webp/blogimages/california/californiasolar.webp",
  ],
  Florida: [
    "/images-webp/blogimages/florida/florida1.webp",
    "/images-webp/blogimages/florida/florida2.webp",
    "/images-webp/blogimages/florida/florida3.webp",
    "/images-webp/blogimages/florida/florida4.webp",
    "/images-webp/blogimages/florida/florida5.webp",
    "/images-webp/blogimages/florida/florida6.webp",
    "/images-webp/blogimages/florida/florida7.webp",
    "/images-webp/blogimages/florida/florida8.webp",
    "/images-webp/blogimages/florida/florida9.webp",
    "/images-webp/blogimages/florida/florida10.webp",
    "/images-webp/blogimages/florida/florida11.webp",
    "/images-webp/blogimages/florida/floridasolar.webp",
  ],
  Texas: [
    "/images-webp/blogimages/texas/texas1.webp",
    "/images-webp/blogimages/texas/texas2.webp",
    "/images-webp/blogimages/texas/texas3.webp",
    "/images-webp/blogimages/texas/texas4.webp",
    "/images-webp/blogimages/texas/texas5.webp",
    "/images-webp/blogimages/texas/texas6.webp",
    "/images-webp/blogimages/texas/texas7.webp",
    "/images-webp/blogimages/texas/texas8.webp",
    "/images-webp/blogimages/texas/texas9.webp",
    "/images-webp/blogimages/texas/texas10.webp",
    "/images-webp/blogimages/texas/texas11.webp",
    "/images-webp/blogimages/texas/texassunshine.webp",
  ],
  "North Carolina": [
    "/images-webp/blogimages/northcarolina/northcarolina1.webp",
    "/images-webp/blogimages/northcarolina/northcarolina2.webp",
    "/images-webp/blogimages/northcarolina/northcarolina3.webp",
    "/images-webp/blogimages/northcarolina/northcarolina4.webp",
    "/images-webp/blogimages/northcarolina/northcarolina5.webp",
    "/images-webp/blogimages/northcarolina/northcarolina6.webp",
    "/images-webp/blogimages/northcarolina/northcarolina7.webp",
    "/images-webp/blogimages/northcarolina/northcarolina8.webp",
    "/images-webp/blogimages/northcarolina/northcarolina9.webp",
    "/images-webp/blogimages/northcarolina/northcarolina10.webp",
    "/images-webp/blogimages/northcarolina/northcarolina11.webp",
  ],
  Colorado: [
    "/images-webp/blogimages/colorado/colorado1.webp",
    "/images-webp/blogimages/colorado/colorado2.webp",
    "/images-webp/blogimages/colorado/colorado3.webp",
    "/images-webp/blogimages/colorado/colorado4.webp",
    "/images-webp/blogimages/colorado/colorado5.webp",
    "/images-webp/blogimages/colorado/colorado6.webp",
    "/images-webp/blogimages/colorado/colorado7.webp",
    "/images-webp/blogimages/colorado/colorado8.webp",
    "/images-webp/blogimages/colorado/colorado9.webp",
    "/images-webp/blogimages/colorado/colorado10.webp",
    "/images-webp/blogimages/colorado/colorado11.webp",
  ],
  "New Jersey": [
    "/images-webp/blogimages/newjersey/newjersey1.webp",
    "/images-webp/blogimages/newjersey/newjersey2.webp",
    "/images-webp/blogimages/newjersey/newjersey3.webp",
    "/images-webp/blogimages/newjersey/newjersey4.webp",
    "/images-webp/blogimages/newjersey/newjersey5.webp",
    "/images-webp/blogimages/newjersey/newjersey6.webp",
    "/images-webp/blogimages/newjersey/newjersey7.webp",
    "/images-webp/blogimages/newjersey/newjersey8.webp",
    "/images-webp/blogimages/newjersey/newjersey9.webp",
    "/images-webp/blogimages/newjersey/newjersey10.webp",
    "/images-webp/blogimages/newjersey/newjersey11.webp",
  ],
  Nevada: [
    "/images-webp/blogimages/nevada/nevada1.webp",
    "/images-webp/blogimages/nevada/nevada2.webp",
    "/images-webp/blogimages/nevada/nevada3.webp",
    "/images-webp/blogimages/nevada/nevada4.webp",
    "/images-webp/blogimages/nevada/nevada5.webp",
    "/images-webp/blogimages/nevada/nevada6.webp",
    "/images-webp/blogimages/nevada/nevada7.webp",
    "/images-webp/blogimages/nevada/nevada8.webp",
    "/images-webp/blogimages/nevada/nevada9.webp",
    "/images-webp/blogimages/nevada/nevada10.webp",
    "/images-webp/blogimages/nevada/nevada11.webp",
  ],
  Massachusetts: [
    "/images-webp/blogimages/boston/boston1.webp",
    "/images-webp/blogimages/boston/boston2.webp",
    "/images-webp/blogimages/boston/boston3.webp",
    "/images-webp/blogimages/boston/boston4.webp",
    "/images-webp/blogimages/boston/boston5.webp",
    "/images-webp/blogimages/boston/boston6.webp",
    "/images-webp/blogimages/boston/boston7.webp",
    "/images-webp/blogimages/boston/boston8.webp",
    "/images-webp/blogimages/boston/boston9.webp",
    "/images-webp/blogimages/boston/boston10.webp",
    "/images-webp/blogimages/boston/boston11.webp",
  ],
  Georgia: [
    "/images-webp/blogimages/georgia/georgia1.webp",
    "/images-webp/blogimages/georgia/georgia2.webp",
    "/images-webp/blogimages/georgia/georgia3.webp",
    "/images-webp/blogimages/georgia/georgia4.webp",
    "/images-webp/blogimages/georgia/georgia5.webp",
    "/images-webp/blogimages/georgia/georgia6.webp",
    "/images-webp/blogimages/georgia/georgia7.webp",
    "/images-webp/blogimages/georgia/georgia8.webp",
    "/images-webp/blogimages/georgia/georgia9.webp",
    "/images-webp/blogimages/georgia/georgia10.webp",
    "/images-webp/blogimages/georgia/georgia11.webp",
  ],
  Illinois: [
    "/images-webp/blogimages/illinois/illinois1.webp",
    "/images-webp/blogimages/illinois/illinois2.webp",
    "/images-webp/blogimages/illinois/illinois3.webp",
    "/images-webp/blogimages/illinois/illinois4.webp",
    "/images-webp/blogimages/illinois/illinois5.webp",
    "/images-webp/blogimages/illinois/illinois6.webp",
    "/images-webp/blogimages/illinois/illinois7.webp",
    "/images-webp/blogimages/illinois/illinois8.webp",
    "/images-webp/blogimages/illinois/illinois9.webp",
    "/images-webp/blogimages/illinois/illinois10.webp",
  ],
  Washington: [
    "/images-webp/blogimages/washington/washington1.webp",
    "/images-webp/blogimages/washington/washington2.webp",
    "/images-webp/blogimages/washington/washington3.webp",
    "/images-webp/blogimages/washington/washington4.webp",
    "/images-webp/blogimages/washington/washington5.webp",
    "/images-webp/blogimages/washington/washington6.webp",
    "/images-webp/blogimages/washington/washington7.webp",
    "/images-webp/blogimages/washington/washington8.webp",
    "/images-webp/blogimages/washington/washington9.webp",
    "/images-webp/blogimages/washington/washington10.webp",
    "/images-webp/blogimages/washington/washington11.webp",
  ],
  "New York": [
    "/images-webp/blogimages/newyork/newyork1.webp",
    "/images-webp/blogimages/newyork/newyork2.webp",
    "/images-webp/blogimages/newyork/newyork3.webp",
    "/images-webp/blogimages/newyork/newyork4.webp",
    "/images-webp/blogimages/newyork/newyork5.webp",
    "/images-webp/blogimages/newyork/newyork6.webp",
    "/images-webp/blogimages/newyork/newyork7.webp",
    "/images-webp/blogimages/newyork/newyork8.webp",
    "/images-webp/blogimages/newyork/newyork9.webp",
    "/images-webp/blogimages/newyork/newyork10.webp",
    "/images-webp/blogimages/newyork/newyork11.webp",
  ],
  Pennsylvania: [
    "/images-webp/blogimages/pennsylvania/pennsylvania1.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania2.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania3.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania4.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania5.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania6.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania7.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania8.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania9.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania10.webp",
    "/images-webp/blogimages/pennsylvania/pennsylvania11.webp",
  ],
  Oregon: [
    "/images-webp/blogimages/oregon/oregon1.webp",
    "/images-webp/blogimages/oregon/oregon2.webp",
    "/images-webp/blogimages/oregon/oregon3.webp",
    "/images-webp/blogimages/oregon/oregon4.webp",
    "/images-webp/blogimages/oregon/oregon5.webp",  
    "/images-webp/blogimages/oregon/oregon6.webp",
    "/images-webp/blogimages/oregon/oregon7.webp",
    "/images-webp/blogimages/oregon/oregon8.webp",
    "/images-webp/blogimages/oregon/oregon9.webp",
    "/images-webp/blogimages/oregon/oregon10.webp",
    "/images-webp/blogimages/oregon/oregon11.webp",
  ],
  Alabama: [
    "/images-webp/blogimages/alabama/alabama1.webp",
    "/images-webp/blogimages/alabama/alabama2.webp",
    "/images-webp/blogimages/alabama/alabama3.webp",
    "/images-webp/blogimages/alabama/alabama4.webp",
    "/images-webp/blogimages/alabama/alabama5.webp",
    "/images-webp/blogimages/alabama/alabama6.webp",
    "/images-webp/blogimages/alabama/alabama7.webp",
    "/images-webp/blogimages/alabama/alabama8.webp",
    "/images-webp/blogimages/alabama/alabama9.webp",
    "/images-webp/blogimages/alabama/alabama10.webp",
    "/images-webp/blogimages/alabama/alabama11.webp",
  ],
  Utah: [
    "/images-webp/blogimages/utah/utah1.webp",
    "/images-webp/blogimages/utah/utah2.webp",
    "/images-webp/blogimages/utah/utah3.webp",
    "/images-webp/blogimages/utah/utah4.webp",
    "/images-webp/blogimages/utah/utah5.webp",
    "/images-webp/blogimages/utah/utah6.webp",
    "/images-webp/blogimages/utah/utah7.webp",
    "/images-webp/blogimages/utah/utah8.webp",
    "/images-webp/blogimages/utah/utah9.webp",
    "/images-webp/blogimages/utah/utah10.webp",
    "/images-webp/blogimages/utah/utah11.webp",
    
  ],
  "South Carolina": [
    "/images-webp/blogimages/southcarolina/southcarolina1.webp",
    "/images-webp/blogimages/southcarolina/southcarolina2.webp",
    "/images-webp/blogimages/southcarolina/southcarolina3.webp",
    "/images-webp/blogimages/southcarolina/southcarolina4.webp",
    "/images-webp/blogimages/southcarolina/southcarolina5.webp",
    "/images-webp/blogimages/southcarolina/southcarolina6.webp",
    "/images-webp/blogimages/southcarolina/southcarolina7.webp",
    "/images-webp/blogimages/southcarolina/southcarolina8.webp",
    "/images-webp/blogimages/southcarolina/southcarolina9.webp",
    "/images-webp/blogimages/southcarolina/southcarolina10.webp",
    "/images-webp/blogimages/southcarolina/southcarolina11.webp",
  ],
  Ohio: [
    "/images-webp/blogimages/ohio/ohio1.webp",
    "/images-webp/blogimages/ohio/ohio2.webp",
    "/images-webp/blogimages/ohio/ohio3.webp",
    "/images-webp/blogimages/ohio/ohio4.webp",
    "/images-webp/blogimages/ohio/ohio5.webp",
    "/images-webp/blogimages/ohio/ohio6.webp",
    "/images-webp/blogimages/ohio/ohio7.webp",
    "/images-webp/blogimages/ohio/ohio8.webp",
    "/images-webp/blogimages/ohio/ohio9.webp",
    "/images-webp/blogimages/ohio/ohio10.webp",
    "/images-webp/blogimages/ohio/ohio11.webp",
  ],
  Idaho: [
    "/images-webp/blogimages/idaho/idaho1.webp",
    "/images-webp/blogimages/idaho/idaho2.webp",
    "/images-webp/blogimages/idaho/idaho3.webp",
    "/images-webp/blogimages/idaho/idaho4.webp",
    "/images-webp/blogimages/idaho/idaho5.webp",
    "/images-webp/blogimages/idaho/idaho6.webp",
    "/images-webp/blogimages/idaho/idaho7.webp",
    "/images-webp/blogimages/idaho/idaho8.webp",
    "/images-webp/blogimages/idaho/idaho9.webp",
    "/images-webp/blogimages/idaho/idaho10.webp",
    "/images-webp/blogimages/idaho/idaho11.webp",
  ],
  Michigan: [
    "/images-webp/blogimages/michigan/michigan1.webp",
    "/images-webp/blogimages/michigan/michigan2.webp",
    "/images-webp/blogimages/michigan/michigan3.webp",
    "/images-webp/blogimages/michigan/michigan4.webp",
    "/images-webp/blogimages/michigan/michigan5.webp",
    "/images-webp/blogimages/michigan/michigan6.webp",
    "/images-webp/blogimages/michigan/michigan7.webp",
    "/images-webp/blogimages/michigan/michigan8.webp",
    "/images-webp/blogimages/michigan/michigan9.webp",
    "/images-webp/blogimages/michigan/michigan10.webp",
    "/images-webp/blogimages/michigan/michigan11.webp",
  ],
  "New Mexico": [
    "/images-webp/blogimages/newmexico/newmexico1.webp",
    "/images-webp/blogimages/newmexico/newmexico2.webp",
    "/images-webp/blogimages/newmexico/newmexico3.webp",
    "/images-webp/blogimages/newmexico/newmexico4.webp",
    "/images-webp/blogimages/newmexico/newmexico5.webp",
    "/images-webp/blogimages/newmexico/newmexico6.webp",
    "/images-webp/blogimages/newmexico/newmexico7.webp",
    "/images-webp/blogimages/newmexico/newmexico8.webp",
    "/images-webp/blogimages/newmexico/newmexico9.webp",
    "/images-webp/blogimages/newmexico/newmexico10.webp",
    "/images-webp/blogimages/newmexico/newmexico11.webp",
  ],
  Virginia: [
    "/images-webp/blogimages/virginia/virginia1.webp",
    "/images-webp/blogimages/virginia/virginia2.webp",
    "/images-webp/blogimages/virginia/virginia3.webp",
    "/images-webp/blogimages/virginia/virginia4.webp",
    "/images-webp/blogimages/virginia/virginia5.webp",
    "/images-webp/blogimages/virginia/virginia6.webp",
    "/images-webp/blogimages/virginia/virginia7.webp",
    "/images-webp/blogimages/virginia/virginia8.webp",
    "/images-webp/blogimages/virginia/virginia9.webp",
    "/images-webp/blogimages/virginia/virginia10.webp",
    "/images-webp/blogimages/virginia/virginia11.webp",
  ],
  Wisconsin: [
    "/images-webp/blogimages/wisconsin/wisconsin1.webp",
    "/images-webp/blogimages/wisconsin/wisconsin2.webp",
    "/images-webp/blogimages/wisconsin/wisconsin3.webp",
    "/images-webp/blogimages/wisconsin/wisconsin4.webp",
    "/images-webp/blogimages/wisconsin/wisconsin5.webp",
    "/images-webp/blogimages/wisconsin/wisconsin6.webp",
    "/images-webp/blogimages/wisconsin/wisconsin7.webp",
    "/images-webp/blogimages/wisconsin/wisconsin8.webp",
    "/images-webp/blogimages/wisconsin/wisconsin9.webp",
    "/images-webp/blogimages/wisconsin/wisconsin10.webp",
    "/images-webp/blogimages/wisconsin/wisconsin11.webp",
  ],
  Minnesota: [
    "/images-webp/blogimages/minnesota/minnesota1.webp",
    "/images-webp/blogimages/minnesota/minnesota2.webp",
    "/images-webp/blogimages/minnesota/minnesota3.webp",
    "/images-webp/blogimages/minnesota/minnesota4.webp",
    "/images-webp/blogimages/minnesota/minnesota5.webp",  
    "/images-webp/blogimages/minnesota/minnesota6.webp",
    "/images-webp/blogimages/minnesota/minnesota7.webp",
    "/images-webp/blogimages/minnesota/minnesota8.webp",
    "/images-webp/blogimages/minnesota/minnesota9.webp",
    "/images-webp/blogimages/minnesota/minnesota10.webp",
    "/images-webp/blogimages/minnesota/minnesota11.webp",
  ],
  default: [
    "/images-webp/blogimages/solarpanel.webp",
    "/images-webp/blogimages/solarfarm.webp",
    "/images-webp/blogimages/solarhome.webp",
    "/images-webp/blogimages/solarfield.webp",
    "/images-webp/blogimages/solarhousebrick.webp",
    "/images-webp/blogimages/solarhouseredtruck.webp",
    "/images-webp/blogimages/solarman.webp",
    "/images-webp/blogimages/utahsolar.webp",
    "/images-webp/blogimages/vivintpalmtree.webp",
    "/images-webp/blogimages/vivintsolar.webp",
    "/images-webp/blogimages/vivintsolarhome.webp",
    "/images-webp/blogimages/badsolarpanel.webp",
    "/images-webp/blogimages/manonroof.webp",
    "/images-webp/blogimages/energysolarfarm.webp",
  ],
};

const imageRotationTracker = new Map<string, { currentIndex: number; usedImages: Set<string>; }>();

export const getStateImage = (state: string): string => {
  // normalize hyphens & spaces → exact key in stateImages
  const cleaned = state.trim().toLowerCase().replace(/-/g, " ");
  const normalizedState = normalizeStateName(cleaned) || "default";

  const fallbackImage = "/images-webp/blog-fallback-solar.webp";
  const imageArray = stateImages[normalizedState as keyof typeof stateImages] || stateImages.default;
  if (!imageArray.length) return fallbackImage;

  let rotationState = imageRotationTracker.get(normalizedState);
  if (!rotationState) {
    rotationState = { currentIndex: 0, usedImages: new Set() };
    imageRotationTracker.set(normalizedState, rotationState);
  }

  if (rotationState.usedImages.size >= imageArray.length) {
    rotationState.usedImages.clear();
  }

  let attempts = 0;
  let nextImage = imageArray[rotationState.currentIndex];
  while (rotationState.usedImages.has(nextImage) && attempts < imageArray.length) {
    rotationState.currentIndex = (rotationState.currentIndex + 1) % imageArray.length;
    nextImage = imageArray[rotationState.currentIndex];
    attempts++;
  }

  rotationState.usedImages.add(nextImage);
  rotationState.currentIndex = (rotationState.currentIndex + 1) % imageArray.length;

  return nextImage || fallbackImage;
};


export default function Blog() {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  const postsPerPage = 6;
  const categories = ["Installation", "Technology", "Economics", "Maintenance"];

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const posts = await getBlogPosts();
        const formattedPosts = posts.map((post) => ({
          ...post,
          date: new Date(post.datePublished).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          category: post.category || "General",
        }));
        setBlogPosts(formattedPosts);
        setFilteredPosts(formattedPosts);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
        setError("Failed to load blog posts. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        const userState = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
        if (userState) filterPosts(searchQuery, userState, blogPosts);
      });
    }
  }, [blogPosts]);

  const filterPosts = (query: string, state: string, sourcePosts = blogPosts) => {
    const queryLower = query.toLowerCase();
    const stateLower = state.toLowerCase();
    const filtered = sourcePosts.filter((post) => {
      const titleMatch = post.title.toLowerCase().includes(queryLower);
      const descriptionMatch = post.description.toLowerCase().includes(queryLower);
      const contentMatch = post.content.toLowerCase().includes(queryLower);
      const keywordsMatch = post.keywords?.some((k) => k.toLowerCase().includes(queryLower));
      const stateMatch = !state || post.state?.toLowerCase() === stateLower;
      return (titleMatch || descriptionMatch || contentMatch || keywordsMatch) && stateMatch;
    });
    setFilteredPosts(filtered);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    filterPosts(searchQuery, "");
    contentRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCategoryChange = (category: string) => {
    if (category === "all") {
      setFilteredPosts(blogPosts);
    } else {
      const filtered = blogPosts.filter((post) => post.category === category);
      setFilteredPosts(filtered);
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    contentRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Error</h2>
          <p className="mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Loading...</h2>
          <p className="mt-2">Please wait while we fetch the blog posts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <section className="blog-module-hero">
          <div className="blog-module-hero-content">
            <h1 className="blog-module-hero-title">Solar Energy Blog</h1>
            <p className="blog-module-hero-subtitle">
              Explore our collection of articles, guides, and insights about
              solar energy and sustainable living.
            </p>
            <div className="blog-module-search">
              <form className="blog-module-search-form" onSubmit={handleSearch}>
                <Input
                  type="search"
                  placeholder="Search articles..."
                  className="blog-module-search-input"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    filterPosts(e.target.value, "");
                  }}
                />
                <Button type="submit" className="blog-module-search-button">
                  <Search className="h-4 w-4" />
                  <span className="sr-only">Search</span>
                </Button>
              </form>
            </div>
          </div>
        </section>

        <section ref={contentRef} className="blog-module-content">
          <div className="blog-module-container">
            <Tabs
              defaultValue="all"
              className="blog-module-tabs"
              onValueChange={(value) => {
                handleCategoryChange(value);
                setCurrentPage(1);
              }}
            >
              <div className="blog-module-tabs-list">
                <TabsList className="tabs-list-custom">
                  <TabsTrigger value="all">All Topics</TabsTrigger>
                  {categories.map((category) => (
                    <TabsTrigger key={category} value={category}>
                      {category}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {["all", ...categories].map((cat) => (
                <TabsContent key={cat} value={cat} className="space-y-8">
                  <div className="blog-module-grid">
                    {currentPosts.map((post, index) =>
                      cat === "all" || post.category?.toLowerCase() === cat.toLowerCase() ? (
                        <Card key={index} className="blog-module-card">
                          <CardHeader className="p-0">
                            <div className="blog-module-card-image">
                              <img
                                src={
                                  post.image?.trim() ||
                                  getStateImage(post.state || "default") ||
                                  "/images/blogimages/herosolar.jpeg"
                                }
                                alt={post.title}
                                className="w-full aspect-video object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = "/images-webp/blog-fallback-solar.webp";
                                }}
                              />
                            </div>
                          </CardHeader>
                          <CardContent className="blog-module-card-content">
                            <div className="blog-module-card-category">{post.category}</div>
                            <CardTitle className="blog-module-card-title">
                              <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                            </CardTitle>
                            <CardDescription className="blog-module-card-excerpt">
                              {post.description}
                            </CardDescription>
                          </CardContent>
                          <CardFooter className="blog-module-card-meta">
                            <div className="blog-module-card-meta-info">
                              <div className="blog-module-card-meta-item">
                                <Calendar className="blog-module-card-meta-icon" />
                                <span>{post.date}</span>
                              </div>
                              <div className="blog-module-card-meta-item">
                                <User className="blog-module-card-meta-icon" />
                                <span>{post.author}</span>
                              </div>
                            </div>
                            <Link to={`/blog/${post.slug}`} className="blog-module-card-link">
                              Read
                            </Link>
                          </CardFooter>
                        </Card>
                      ) : null
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div className="blog-pagination">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-button"
                      >
                        Previous
                      </button>
                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-button"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </section>
      </div>
    </div>
  );
}
